import { IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Credit } from 'lib/types/credits';
import { userIsAdmin } from 'lib/helpers';
import { useCurrentUser, useApi } from 'lib/contexts/ApplicationState';
import { CreditItem } from 'components/CreditItem';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { useModal } from 'mui-modal-provider';
import { MobileTooltip } from 'components/MobileTooltip';
import { useQuery } from '@tanstack/react-query';

interface Props {
  credit: Credit;
  onStartEdit: () => void;
  hideEdit?: boolean;
}

export const EditableCreditItem = ({
  credit,
  onStartEdit,
  hideEdit,
}: Props) => {
  const currentUser = useCurrentUser();
  const creditUser = credit?.user;
  const { showModal } = useModal();
  const API = useApi();

  const { data: creditCreator } = useQuery({
    queryKey: ['getCreditCreator', credit.id],
    queryFn: () => API.getCreditCreator(credit.id),
    enabled: !!currentUser && !!credit.id,
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  });

  const currentUserIsCreator = creditCreator?.userId === currentUser?.id;

  const canEditCredit = !hideEdit && currentUser && (
    userIsAdmin(currentUser) || (creditUser && currentUser.id === creditUser.id)
      || currentUserIsCreator);

  const onClickEdit = () => {
    if (currentUser?.id !== creditUser.id
      && !userIsAdmin(currentUser)
      && !currentUserIsCreator) {
      const modal = showModal(ConfirmationModal, {
        text: 'Are you sure you want to edit this credit? Please only do so if this to correct an inaccuracy.',
        onNo: () => {
          modal.destroy();
        },
        onYes: () => {
          onStartEdit();
          modal.destroy();
        },
      });
    } else {
      onStartEdit();
    }
  };

  return (
    <div className="flex justify-between p-2">
      <CreditItem
        credit={credit}
      />
      <span className="w-auto h-auto my-auto">
        {
          canEditCredit && (
            <IconButton
              color="primary"
              aria-label="edit credit"
              onClick={onClickEdit}
              size="large"
            >
              <EditIcon fontSize="small" sx={{ zIndex: 0 }} />
            </IconButton>
          )
        }
        {
          !canEditCredit && credit.disputed && (
            <MobileTooltip
              title="This credit is under review"
              arrow
            >
              <ReportProblemOutlinedIcon />
            </MobileTooltip>
          )
        }
      </span>
    </div>
  );
};
